<template>
  <div v-if="editor">
    <editor-menu-bubble
      :editor="editor"
      :keep-in-bounds="true"
      v-slot="{commands, isActive, menu, getMarkAttrs}"
    >
      <div
        class="
          menububble
          flex
          items-center
          p-2
          rounded
          bg-white
          shadow-md
          border border-dull-light
        "
        :class="{'is-active': menu.isActive}"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
      >
        <Typography variant="label" class="mr-2"> Text </Typography>
        <ColorPicker
          v-if="settings.color"
          v-slot="{handleClick, value}"
          @input="commands.color({color: arguments[0]})"
          :swatches="swatches"
        >
          <IconButton
            class="relative block mr-2"
            :class="{'is-active': getMarkAttrs('color').color}"
            @click="handleClick"
          >
            <Icon name="paint-brush" class="text-secondary" />
            <div
              class="font-color-underline"
              :style="{'background-color': getMarkAttrs('color').color}"
            ></div>
          </IconButton>
        </ColorPicker>

        <LinkMenu
          v-if="settings.link"
          v-slot="{handleOpen, value}"
          @input="commands.link(arguments[0])"
          :value="getMarkAttrs('link')"
        >
          <IconButton
            class="relative mr-2"
            :class="{'is-active': getMarkAttrs('link').href}"
            @click="handleOpen"
          >
            <Icon name="link" class="text-secondary" />
          </IconButton>
        </LinkMenu>

        <IconButton
          v-if="settings.bold"
          class="mr-2"
          :class="{'is-active': isActive.bold()}"
          @click="commands.bold"
        >
          <Icon name="bold" class="text-secondary" />
        </IconButton>

        <IconButton
          v-if="settings.italic"
          class="mr-2"
          :class="{'is-active': isActive.italic()}"
          @click="commands.italic"
        >
          <Icon name="italic" class="text-secondary" />
        </IconButton>

        <IconButton
          v-if="settings.strike"
          class="mr-2"
          :class="{'is-active': isActive.strike()}"
          @click="commands.strike"
        >
          <Icon name="strikethrough" class="text-secondary" />
        </IconButton>

        <IconButton
          v-if="settings.underline"
          :class="{'is-active': isActive.underline()}"
          @click="commands.underline"
        >
          <Icon name="underline" class="text-secondary" />
        </IconButton>

        <HighlightPicker
          v-if="settings.highlight"
          v-slot="{handleOpen, value}"
          @input="commands.highlight($event)"
          :value="getMarkAttrs('highlight')"
          :swatches="swatches"
        >
          <IconButton
            class="relative mr-2"
            :class="{'is-active': getMarkAttrs('highlight').highlight}"
            @click="handleOpen"
          >
            <Icon name="highlighter" class="text-secondary" />
            <div
              class="font-color-underline"
              :style="{'background-color': getMarkAttrs('highlight').color}"
            ></div>
          </IconButton>
        </HighlightPicker>
      </div>
    </editor-menu-bubble>

    <div :style="editorPosition" v-if="editor" class="editor-wrapper">
      <editor-content class="editor__content" :editor="editor" />
    </div>
  </div>
</template>

<script>
import '@converdy/styles/base.scss';

import {Editor, EditorContent, EditorMenuBubble} from 'tiptap';
import Button from '@converdy/atoms/Button';
import {
  HardBreak,
  Bold,
  Italic,
  Strike,
  Underline,
  History,
  Placeholder,
} from 'tiptap-extensions';

import Color from '@converdy/components/TextEditor/marks/Color';
import Link from '@converdy/components/TextEditor/marks/Link';
import Highlight from '@converdy/components/TextEditor/marks/Highlight';
import InlineSingleLineDoc from '@converdy/components/TextEditor/docs/InlineSingleLineDoc';
import InlineParagraphDoc from '@converdy/components/TextEditor/docs/InlineParagraphDoc';

import ColorPicker from '@converdy/components/TextEditor/poppers/ColorPicker';
import HighlightPicker from '@converdy/components/TextEditor/poppers/HighlightPicker';
import LinkMenu from '@converdy/components/TextEditor/poppers/LinkMenu';
import Icon from '@converdy/atoms/Icon';
import IconButton from '@converdy/atoms/IconButton';

export default {
  components: {
    EditorContent,
    Button,
    HighlightPicker,
    EditorContent,
    EditorMenuBubble,
    Icon,
    IconButton,
    ColorPicker,
    LinkMenu,
  },
  props: {
    editorPosition: {
      type: Object,
    },
    content: {
      type: String,
      default: '',
    },
    options: {
      type: Object,
    },
    // placeholder: {
    //   default: 'Your text...',
    //   type: String,
    // },
  },
  data() {
    return {
      swatches: [
        {
          name: 'Purple',
          value: 'rgb(233, 227, 255)',
        },
        {
          name: 'Yellow',
          value: 'rgb(233,227,275)',
        },
        {
          name: 'Light Blue',
          value: '#d7e9ff',
        },
        {
          name: 'Light Blue',
          value: '#b5d9ff',
        },
        {
          name: 'Light Pink',
          value: 'rgb(253, 242, 248)',
        },
      ],
      editor: false,
      settings: {},
      defaultOptions: {
        editorPosition: true,
        content: true,
        hardbreak: true,
        link: true,
        singleLine: true,
        highlight: true,
        color: true,
        bold: true,
        italic: true,
        strike: true,
        underline: true,
        history: true,
        placeholder: 'Start Typing',
      },
    };
  },

  watch: {
    content(html) {
      if (this.editor) {
        this.editor.setContent(html);
      }
    },
    options(options) {
      this.destroyEditor();
      this.settings = {...this.defaultOptions, ...options};
      this.safeCreate();
    },
  },

  created() {
    this.settings = {...this.defaultOptions, ...this.options};
    this.safeCreate();
  },

  beforeDestroy() {
    this.destroyEditor();
  },
  methods: {
    // ugly workaround for unpredictable tiptap error...
    safeCreate() {
      try {
        this.createEditor();
      } catch (e) {
        this.createEditor();
      }
    },
    createEditor() {
      const extensions = [
        // new Placeholder({
        //   emptyEditorClass: 'is-editor-empty',
        //   emptyNodeClass: 'is-empty',
        //   emptyNodeText: 'Write something …',
        //   showOnlyWhenEditable: true,
        //   showOnlyCurrent: true,
        // }),
        /* Order defines priority, keep Color high! */
      ];

      if (this.settings.singleLine) {
        extensions.push(new InlineSingleLineDoc());
      } else {
        extensions.push(new InlineParagraphDoc());
      }

      if (this.settings.highlight) {
        extensions.push(new Highlight());
      }

      if (this.settings.color) {
        extensions.push(new Color());
      }

      if (this.settings.bold) {
        extensions.push(new Bold());
      }
      if (this.settings.italic) {
        extensions.push(new Italic());
      }
      if (this.settings.strike) {
        extensions.push(new Strike());
      }
      if (this.settings.history) {
        extensions.push(new History());
      }
      if (this.settings.underline) {
        extensions.push(new Underline());
      }

      if (this.settings.hardbreak) {
        extensions.push(new HardBreak());
      }

      if (this.settings.link) {
        extensions.push(new Link());
      }

      this.editor = new Editor({
        content: this.content,
        extensions,
        onBlur: (obj) => {
          this.focused = false;
          this.$emit('blur');
        },
        onFocus: () => {
          this.focused = true;
          this.$emit('focus');
        },
        onUpdate: ({getHTML, getJSON}) => {
          const html = getHTML();

          this.$emit('input', html);
        },
      });

      this.editor.focus();
    },
    destroyEditor() {
      this.editor && this.editor.destroy();
    },
  },
};
</script>

<style lang="scss">
/* old */

.editor__content {
  width: 100%;
}
.font-color-underline {
  width: 20px;
  height: 3px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background: black;
}

.menububble {
  position: absolute;
  z-index: 20;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
}

.menububble .is-active {
  @apply bg-dull-light;
}

.menububble.is-active {
  opacity: 1;
  visibility: visible;
  pointer-events: all;
}

.editor .is-editor-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}

.ProseMirror {
  cursor: text;
  &:focus {
    outline: none;
  }
}
</style>
